import React from 'react';
import { useSearchParams } from 'react-router';
import { TypeBasedSearchParamsSerializer } from '../routing/TypeBasedSearchParamsSerializer';
import { ISearchParamsSerializer } from '../routing/routing.types';


export type IFilterContext<T> = {
    defaultValue: T,
    filterValue: T,
    applyFilter: (filterValue: T) => void,
}

export const createFilterContext = <T,>() => React.createContext<IFilterContext<T> | null>(null);
export const createUseFilterContext = <T,>(Context: React.Context<IFilterContext<T> | null>) => (): IFilterContext<T> => {
    if (Context === null) {
        throw new Error('No FilterContext provided');
    }

    const result = React.useContext(Context);

    if (result === null) {
        throw new Error('No FilterContext provided');
    } else {
        return result;
    }
};

export type IFilterProviderProps<T> = {
    defaultValue: T,
    useQueryParams?: boolean,
    searchParamsSerializer?: ISearchParamsSerializer,
}

export const createWithFilter = <T,>(Context: React.Context<IFilterContext<T> | null>) => {
    const resultFn = <P extends object,>({
        defaultValue,
        useQueryParams = true,
        searchParamsSerializer = new TypeBasedSearchParamsSerializer(),
    }: IFilterProviderProps<T>,
        Component: React.FC<P>): React.FC<P> =>
        (props) => {

            const [searchParams, setSearchParams] = useSearchParams();
            const searchValue = useQueryParams ? searchParamsSerializer.decodeSearchParams(searchParams) : {};
            const [filterValue, setFilterValue] = React.useState<T>({ ...defaultValue, ...searchValue });

            const applyFilter = (newFilterValue: T) => {
                setFilterValue(newFilterValue);
                if (useQueryParams) {
                    const newSearchParams = searchParamsSerializer.transformToSearchParams(newFilterValue);
                    for (const key in newSearchParams) {
                        const val = newSearchParams[key];
                        if (val === null || val === undefined) {
                            searchParams.delete(key);
                        } else if (Array.isArray(val)) {
                            searchParams.delete(key);
                            val.forEach(aVal => {
                                searchParams.append(key, aVal);
                            });
                        } else {
                            searchParams.set(key, val);
                        }
                    }
                    setSearchParams(searchParams);
                }
            }

            return (
                <Context.Provider
                    value={{
                        defaultValue,
                        filterValue,
                        applyFilter,
                    }}
                >
                    <Component {...(props) as P} />
                </Context.Provider>
            )
        };


    return resultFn;
}




export const createWithFilterContext = <T, P extends object>(useCtx: () => IFilterContext<T>) => (
    Component: React.ComponentType<P>,
): React.ComponentType<Omit<P, keyof IFilterContext<T>>> => props => {

    const filterContext = useCtx();

    return (
        <Component {...{ ...props, ...filterContext } as P} />
    )
};



export const createFilterExports = <T, P extends object>(Component: React.ComponentType<P>) => {
    const filterContext = createFilterContext<T>();
    const useFilterContext = createUseFilterContext<T>(filterContext);
    const withFilterContext = createWithFilterContext<T, P>(useFilterContext);

    return {
        PureComponent: Component,
        Component: withFilterContext(Component),
        withProvider: createWithFilter<T>(filterContext),
        useContext: useFilterContext,
    }
}